import React, { useMemo, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ApiEndpoint, PermissionPage } from '../../services/permissions.service';
import ThreeStateToggle from '../ThreeStateToggle';
import TypeAhead, { TypeAheadOption } from '../TypeAhead';
import {
  GrantedFilter,
  GRANTED_FILTER_LABELS,
  grantedFilterToToggle,
  toggleToGrantedFilter,
  passesGrantedFilter,
} from './grantedFilter';

interface NonPageRelatedViewProps {
  apiEndpoints: Record<string, ApiEndpoint[]>;
  apiEndpointPermissions: Map<number, { can_read: boolean; can_write: boolean }>;
  permissionPages: PermissionPage[];
  onAssignEndpointToPage: (endpointId: number, pageRoute: string, required: boolean) => void;
  onToggleApiAccess: (endpointId: number, method: string) => void;
}

const methodColors: Record<string, string> = {
  GET: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  POST: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  PUT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  DELETE: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  PATCH: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
};

export const NonPageRelatedView: React.FC<NonPageRelatedViewProps> = ({
  apiEndpoints,
  apiEndpointPermissions,
  permissionPages,
  onAssignEndpointToPage,
  onToggleApiAccess,
}) => {
  const [searchFilter, setSearchFilter] = useState('');
  const [grantedFilter, setGrantedFilter] = useState<GrantedFilter>('all');
  const [pageChoice, setPageChoice] = useState<Record<number, string>>({});
  const [requiredChoice, setRequiredChoice] = useState<Record<number, boolean>>({});
  const [categoryPageChoice, setCategoryPageChoice] = useState<Record<string, string>>({});

  // An endpoint is "covered" iff it appears under at least one page.
  // Server-side already does the resolution — we just walk the response.
  const coveredEndpointIds = useMemo(() => {
    const set = new Set<number>();
    for (const page of permissionPages) {
      for (const ep of page.endpoints) {
        set.add(ep.endpoint_id);
      }
    }
    return set;
  }, [permissionPages]);

  const searchTokens = useMemo(
    () => searchFilter.toLowerCase().split(/\s+/).filter(Boolean),
    [searchFilter]
  );

  const unassignedEndpoints = useMemo(() => {
    const flat = Object.values(apiEndpoints).flat();
    const rows: Array<{ endpoint: ApiEndpoint }> = [];
    for (const endpoint of flat) {
      if (coveredEndpointIds.has(endpoint.id)) continue;
      if (grantedFilter !== 'all') {
        // Inline the grant check — hasAccess() is declared below this memo.
        const perm = apiEndpointPermissions.get(endpoint.id);
        const granted = perm ? (endpoint.method === 'GET' ? perm.can_read : perm.can_write) : false;
        if (!passesGrantedFilter(grantedFilter, granted)) continue;
      }
      if (searchTokens.length > 0) {
        const haystack = [
          endpoint.path,
          endpoint.method,
          endpoint.name,
          endpoint.description,
          endpoint.category,
          endpoint.subcategory,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!searchTokens.every(t => haystack.includes(t))) continue;
      }
      rows.push({ endpoint });
    }
    rows.sort((a, b) => {
      const cat = (a.endpoint.category || '').localeCompare(b.endpoint.category || '');
      if (cat !== 0) return cat;
      const path = a.endpoint.path.localeCompare(b.endpoint.path);
      if (path !== 0) return path;
      return a.endpoint.method.localeCompare(b.endpoint.method);
    });
    return rows;
  }, [apiEndpoints, coveredEndpointIds, searchTokens, grantedFilter, apiEndpointPermissions]);

  const assignablePages = useMemo(() => {
    return [...permissionPages].sort((a, b) => {
      const m = a.module.localeCompare(b.module);
      if (m !== 0) return m;
      return a.route_name.localeCompare(b.route_name);
    });
  }, [permissionPages]);

  // Type-ahead options for the two "Assign to page…" pickers (~200 pages is
  // unusable as a native select). Grouped by module; searching matches the
  // page name and module.
  const pageOptions = useMemo<TypeAheadOption[]>(
    () =>
      assignablePages.map(p => ({
        value: p.route_path,
        label: p.route_name,
        description: p.route_path,
        group: p.module,
      })),
    [assignablePages]
  );

  const rowsByCategory = useMemo(() => {
    const groups = new Map<string, Array<{ endpoint: ApiEndpoint }>>();
    unassignedEndpoints.forEach(row => {
      const key = row.endpoint.category || 'Other';
      const list = groups.get(key) ?? [];
      list.push(row);
      groups.set(key, list);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [unassignedEndpoints]);

  const hasAccess = (endpoint: ApiEndpoint): boolean => {
    const perm = apiEndpointPermissions.get(endpoint.id);
    if (!perm) return false;
    return endpoint.method === 'GET' ? perm.can_read : perm.can_write;
  };

  const handleAssignAll = (category: string, rows: Array<{ endpoint: ApiEndpoint }>) => {
    const route = categoryPageChoice[category];
    if (!route) return;
    rows.forEach(({ endpoint }) => {
      onAssignEndpointToPage(endpoint.id, route, false);
    });
    setCategoryPageChoice(prev => {
      const next = { ...prev };
      delete next[category];
      return next;
    });
  };

  const handleAssign = (endpointId: number) => {
    const route = pageChoice[endpointId];
    if (!route) return;
    onAssignEndpointToPage(endpointId, route, requiredChoice[endpointId] ?? false);
    setPageChoice(prev => {
      const next = { ...prev };
      delete next[endpointId];
      return next;
    });
    setRequiredChoice(prev => {
      const next = { ...prev };
      delete next[endpointId];
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-900/20 p-3 text-xs text-blue-900 dark:text-blue-200">
        These endpoints are registered in the backend but aren't assigned to any frontend page.
        Until they're assigned, they only appear here, and admins doing per-page grants can't see
        them. Granting access works either way.
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            placeholder="Search non-page-related endpoints..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
        <div
          className="flex items-center gap-2 whitespace-nowrap"
          title="All: show everything · Hide Granted: only what's missing · Only Granted: only what this group has"
        >
          <span className="text-xs text-muted">Show:</span>
          <ThreeStateToggle
            size="sm"
            ariaLabel="Filter endpoints by grant state"
            value={grantedFilterToToggle(grantedFilter)}
            onChange={(v) => setGrantedFilter(toggleToGrantedFilter(v))}
            labels={GRANTED_FILTER_LABELS}
          />
        </div>
      </div>

      {unassignedEndpoints.length === 0 ? (
        <p className="text-sm text-muted italic p-3">
          {searchFilter
            ? 'No non-page-related endpoints match your search.'
            : grantedFilter === 'hide-granted'
              ? 'This group already has every non-page-related endpoint.'
              : grantedFilter === 'only-granted'
                ? 'This group has no non-page-related endpoints granted yet.'
                : 'Every registered endpoint is assigned to a page. Nothing here.'}
        </p>
      ) : (
        rowsByCategory.map(([category, rows]) => (
          <div
            key={category}
            className="border border-gray-200 dark:border-gray-700 rounded-lg"
          >
            <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800/60 rounded-t-lg">
              <h3 className="text-sm font-semibold text-heading flex-1 min-w-0">
                {category}
              </h3>
              <span className="text-xs text-muted shrink-0">
                {rows.length} endpoint{rows.length !== 1 ? 's' : ''}
              </span>
              <div className="w-64 shrink-0">
                <TypeAhead
                  options={pageOptions}
                  value={categoryPageChoice[category] ?? ''}
                  onChange={value => setCategoryPageChoice(prev => ({ ...prev, [category]: value }))}
                  placeholder="Assign to page…"
                  ariaLabel={`Assign all ${category} endpoints to a page`}
                  groupBy={true}
                  compact
                  maxHeight="max-h-80"
                />
              </div>
              <button
                disabled={!categoryPageChoice[category]}
                onClick={() => handleAssignAll(category, rows)}
                className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/20 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                Assign All
              </button>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2 space-y-1">
              {rows.map(({ endpoint }) => {
                const granted = hasAccess(endpoint);
                const selectedPage = pageChoice[endpoint.id] ?? '';
                const required = requiredChoice[endpoint.id] ?? false;
                return (
                  <div key={endpoint.id} className="flex flex-wrap items-center gap-2 py-1">
                    <input
                      type="checkbox"
                      checked={granted}
                      onChange={() => onToggleApiAccess(endpoint.id, endpoint.method)}
                      className="h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      title="Grant this endpoint to the selected group"
                    />
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${methodColors[endpoint.method] || 'bg-gray-100 text-gray-800'}`}>
                      {endpoint.method}
                    </span>
                    <span className="text-xs text-label flex-1 min-w-0 break-all">
                      {endpoint.path}
                    </span>
                    <div className="w-64 shrink-0">
                      <TypeAhead
                        options={pageOptions}
                        value={selectedPage}
                        onChange={value => setPageChoice(prev => ({ ...prev, [endpoint.id]: value }))}
                        placeholder="Assign to page…"
                        ariaLabel={`Assign ${endpoint.method} ${endpoint.path} to a page`}
                        groupBy={true}
                        compact
                        maxHeight="max-h-80"
                      />
                    </div>
                    <label className="text-xs text-gray-600 dark:text-gray-400 inline-flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={required}
                        onChange={e => setRequiredChoice(prev => ({ ...prev, [endpoint.id]: e.target.checked }))}
                        className="h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      Required
                    </label>
                    <button
                      disabled={!selectedPage}
                      onClick={() => handleAssign(endpoint.id)}
                      className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Assign
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};
